
import Vue from "vue";
import {mapState} from "vuex";
import {Article, Dossier} from "@/api/flame";
import draggable from "vuedraggable";
import {ContactRelationResult} from "@/api/contactRelation";
import {findConfirmDialogRef} from "@/utils/RefUtils";

export interface ProductFormInputDTO {
    dossier: Dossier | null;
}

let articleKeyCounter = 0;

export default Vue.extend({
    components: {
        draggable,
    },
    props: {
        productFormInput: {
            type: Object as () => ProductFormInputDTO,
            required: true,
        },
        isWorking: {
            type: Boolean,
        },
        readonly: {
            type: Boolean,
        },
    },
    data() {
        return {
            dossier: {} as Dossier,
            productDialog: false,
            currentArticles: [] as Article[],
            articleKeys: new WeakMap<Article, string>(),
            headersArticleColumns: [
                {
                    text: this.$t("Produkt"),
                    sortable: false,
                    width: "25%",
                    value: "product",
                },
                {
                    text: this.$t("Menge"),
                    sortable: false,
                    width: "5%",
                },
                {
                    text: this.$t("Nachlass"),
                    sortable: false,
                    width: "10%",
                },
                {
                    text: this.$t("Lager"),
                    sortable: false,
                    width: "10%",
                },
                {
                    text: this.$t("Hinweis"),
                    sortable: false,
                    width: "45%",
                },
                {
                    text: this.$t("Aktionen"),
                    sortable: false,
                    width: "10%",
                },
            ],
            resellerContacts: [] as ContactRelationResult[],
        };
    },
    computed: {
        ...mapState("product", ["productsOnSale"]),
        ...mapState("warehouse", ["warehouses"]),
    },
    watch: {
        productFormInput: {
            async handler() {
                this.resetData();
                if (this.productFormInput.dossier) {
                    this.dossier = this.productFormInput.dossier;
                    this.init();
                }
            },
            immediate: true,
        },
    },
    async created() {
        await this.$store.dispatch("product/listAll");
    },
    methods: {
        resetData() {
            this.currentArticles = []
            this.resellerContacts = []
        },
        async init() {
            if (this.dossier.articles) {
                this.currentArticles = [...this.dossier.articles].sort(
                    (a: Article, b: Article) =>
                        (a.orderKey ?? Number.MAX_SAFE_INTEGER) - (b.orderKey ?? Number.MAX_SAFE_INTEGER)
                );
            }
        },
        async removeArticle(index: number) {
            // eslint-disable-next-line
            const res = await (findConfirmDialogRef(this) as any).open(
                this.$t("Achtung") as string,
                this.$t("Wirklich den Artikel entfernen?") as string,
                {
                    textColor: "red",
                    textOk: "Ja",
                    textCancel: "Nein und zurück",
                    secondOkButton: false,
                    noconfirm: false,
                }
            );
            if (res) {
                this.currentArticles.splice(index, 1);
                this.updateProduct();
            }
        },
        addEmptyArticle() {
            if (!this.currentArticles) {
                this.currentArticles = [];
            }
            this.currentArticles.push({
                productName: "",
                quantity: 1,
                discount: null,
                orderKey: this.currentArticles.length + 1,
                warehouse: null,
                description: null,

            } as Article);
            this.updateProduct();
        },
        updateProduct() {
            this.normalizeArticleOrder();
            this.dossier.articles = this.currentArticles;
            this.$emit("articleChanged", this.currentArticles);
        },
        normalizeArticleOrder() {
            this.currentArticles.forEach((article: Article, index: number) => {
                article.orderKey = index + 1;
            });
        },
        articleKey(article: Article): string {
            // Vuetify inputs watch their `value` prop and copy it into their internal lazyValue.
            // With index keys an input is reused for a different article after a drag, which
            // makes rows show values of other articles. A stable key per article object keeps
            // each row bound to its own article.
            if (!this.articleKeys.has(article)) {
                this.articleKeys.set(article, `article-${++articleKeyCounter}`);
            }
            return this.articleKeys.get(article) as string;
        },
        async updateDossier() {
            this.$emit("dossierChanged", this.dossier);
        },
        moveArticle(evt: { moved?: { oldIndex: number; newIndex: number } }) {
            if (!evt.moved) {
                return;
            }
            // vuedraggable already reordered currentArticles; persist the new order as 1..n
            this.updateProduct();
        },
    },
});
